import { useEffect, useMemo, useState } from "react";
import NavBar from "../../component/navBar/NavBar";
import { useAuth } from "../../config/utilities/useAuth";
import {
  APICreateTrip,
  APIDeleteTrips,
  APIGetTrips,
  APIUpdateTrip,
} from "../../config/utilities/APIs/tripsApi";
import DeleteModal from "../../component/modal/DeleteModal";
import Title from "../../component/title/Title";
import { TripForm } from "./TripForm";
import Table from "../../component/table/Table";
import SnackBar from "../../component/Snackbar/Snackbar";
import DeactivationModal from "../../component/modal/DeactivationModal";
import { format, parse } from "date-fns";
import { useTimezone } from "../../config/utilities/useTimezone";
import { getUTCOffset } from "../../config/utilities/fDate";
import "./Trips.css";

const statusOptions = [
  { label: "Active", value: "active" },
  { label: "Inactive", value: "inactive" },
];

export default function Trips() {
  const { privilege, token } = useAuth();
  const role = privilege?.role;
  const institutionId = privilege?.institutionId;
  const [action, setAction] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showSnackbar, setShowSnackbar] = useState(false);
  const [snackbarType, setSnackbarType] = useState("");
  const [snackbarTitle, setSnackbarTitle] = useState("");
  const [snackbarSubtitle, setSnackbarSubtitle] = useState("");
  const [selectedRowData, setSelectedRowData] = useState(null);
  const [fetchedTripData, setFetchedTripData] = useState({
    trips: [],
    pagination: {},
  });
  const [pageNumber, setPageNumber] = useState(1);
  const [selectedFilters, setSelectedFilters] = useState({
    status: "",
  });
  const [searchTerm, setSearchTerm] = useState("");
  const [showDeactivationModal, setShowDeactivationModal] = useState(false);

  const filters = [
    { name: "status", placeholder: "Filter by Status", options: statusOptions },
  ];

  const [input, setInput] = useState({
    tripName: "",
    type: "",
    startTime: "",
    stopsArray: [],
    tripLegsArray: [],
    status: "active",
    updateScope: "none",
  });

  const [error, setError] = useState({
    tripName: false,
    stopsArray: false,
    type: false,
    startTime: false,
  });

  const { timezone } = useTimezone();
  const headers = useMemo(() => {
    const offset = getUTCOffset(timezone);

    return [
      { key: "name", name: "Trip Name", sortable: false },
      { key: "type", name: "Trip Type", sortable: false },
      { key: "startTime", name: `Start Time (${offset})`, sortable: false },
      { key: "stopsAmount", name: "No of Stops", sortable: false },
      { key: "totalStudents", name: "No of Students", sortable: false },
      { key: "status", name: "Status", sortable: false },
    ];
  }, [timezone]);

  const fetchTrips = async (
    page = 1,
    filters = selectedFilters,
    searchTerm,
  ) => {
    setLoading(true);

    try {
      const filterParams = Object.fromEntries(
        Object.entries(filters).filter(([_, v]) => v !== ""),
      );
      const response = await APIGetTrips(
        page,
        filterParams,
        setLoading,
        institutionId,
        searchTerm,
      );

      if (response.status === 200) {
        const { data } = response.data;
        setFetchedTripData(data);
        setPageNumber(page);
      } else if (response.status === 400) {
        console.warn("Bad Request:", response);
      } else if (response.status === 401) {
        console.warn("Unauthorized:", response);
      } else if (response.status === 403) {
        console.warn("Forbidden:", response);
      } else if (response.status === 404) {
        console.warn("Not Found:", response);
      } else if (response.status >= 500) {
        console.error("Server Error:", response);
      } else {
        console.warn("Unexpected Status Code:", response.status, response);
      }
    } catch (error) {
      console.error("Error fetching trips:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTrips(1, selectedFilters, searchTerm);
  }, [selectedFilters, searchTerm]);

  const handleReset = async () => {
    setInput({
      tripName: "",
      type: "",
      startTime: "",
      stopsArray: [],
      tripLegsArray: [],
      status: "active",
      updateScope: "none",
    });
    setError({
      tripName: false,
      type: false,
      startTime: false,
      stopsArray: false,
      status: false,
    });
    setFetchedTripData({ trips: [], pagination: {} });
    setShowDeleteModal(false);
    setShowDeactivationModal(false);
    setSelectedRowData(null);
    setAction(null);
    setPageNumber(1);
    await fetchTrips(1, selectedFilters, searchTerm);
  };

  const handleDelete = async (row) => {
    setLoading(true);

    const rowId = row._id;

    try {
      const response = await APIDeleteTrips(
        rowId,
        setLoading,
        institutionId,
        token,
      );

      if (response?.status === 200) {
        setShowSnackbar(true);
        setSnackbarTitle("Delete Successful");
        setSnackbarSubtitle("Trip has been deleted successfuly");
        setSnackbarType("success");
      } else {
        throw new Error(
          response?.response?.data?.message || "Failed to delete trip",
        );
      }
    } catch (error) {
      setShowSnackbar(true);
      setSnackbarTitle("Delete Failed");
      setSnackbarSubtitle(error.message || "An error occurred");
      setSnackbarType("error");
    } finally {
      handleReset();
    }
  };

  const handleEdit = async () => {
    setLoading(true);

    const formattedInput = formatInput(input, "edit");

    try {
      const response = await APIUpdateTrip(
        formattedInput,
        setLoading,
        institutionId,
        token,
      );

      if (response?.status === 200) {
        setShowSnackbar(true);
        setSnackbarTitle("Update Successful");
        setSnackbarSubtitle("Trip has been updated successfuly");
        setSnackbarType("success");
      } else {
        throw new Error(
          response?.response?.data?.message || "Failed to update trip",
        );
      }
    } catch (error) {
      setShowSnackbar(true);
      setSnackbarTitle("Update Failed");
      setSnackbarSubtitle(error.message || "An error occurred");
      setSnackbarType("error");
    } finally {
      handleReset();
    }
  };

  const handleCreate = async () => {
    setLoading(true);

    const formattedInput = formatInput(input, "create");

    try {
      const response = await APICreateTrip(
        formattedInput,
        setLoading,
        institutionId,
        token,
      );

      if (response?.status === 200) {
        setShowSnackbar(true);
        setSnackbarTitle("Create Successful");
        setSnackbarSubtitle("Trip has been created successfuly");
        setSnackbarType("success");
      } else {
        throw new Error(
          response?.response?.data?.message || "Failed to create trip",
        );
      }
    } catch (error) {
      setShowSnackbar(true);
      setSnackbarTitle("Create Failed");
      setSnackbarSubtitle(error.message || "An error occurred");
      setSnackbarType("error");
    } finally {
      handleReset();
    }
  };

  const handleStatus = async (action) => {
    setLoading(true);
    let status;
    let title;
    if (action === "activate") {
      status = "active";
      title = "Activation";
    } else {
      status = "inactive";
      title = "Deactivation";
    }

    const payload = { status, _id: selectedRowData._id };
    try {
      await APIUpdateTrip(payload, setLoading, institutionId, token);
    } catch (error) {
      setShowSnackbar(true);
      setSnackbarTitle(`${title} Failed`);
      setSnackbarSubtitle(error.message || "An error occurred");
      setSnackbarType("error");
    } finally {
      handleReset();
    }
  };

  const handlePopoverAction = (action, rowId) => {
    const selectedRow = fetchedTripData.trips.find((row) => row._id === rowId);
    if (action === "edit") {
      setSelectedRowData(selectedRow);
      setAction("edit");
    } else if (action === "delete") {
      setSelectedRowData(selectedRow);
      setShowDeleteModal(true);
    } else if (action === "activate") {
      setSelectedRowData(selectedRow);
      setAction("activate");
      setShowDeactivationModal(true);
    } else if (action === "deactivate") {
      setSelectedRowData(selectedRow);
      setAction("deactivate");
      setShowDeactivationModal(true);
    } else {
      setSelectedRowData(selectedRow);
      setAction("view");
    }
  };

  const handleOpenCreate = () => {
    setAction("create");
  };

  const formatInput = (rawInput, mode) => {
    return {
      ...(rawInput._id && { _id: rawInput._id }),
      ...(mode === "edit" && { updateScope: rawInput.updateScope }),
      name: rawInput.tripName,
      type: rawInput.type,
      startTime: rawInput.startTime,
      stopsArray: rawInput.stopsArray?.map(
        ({ totalChildCount, associatedChilds, eta, ...rest }) => {
          let formattedEta = eta;
          if (eta) {
            try {
              const date = parse(eta, "HH:mm", new Date());
              formattedEta = format(date, "hh:mm:ss a");
            } catch {
              formattedEta = eta;
            }
          }

          return { ...rest, eta: formattedEta };
        },
      ),
      status: rawInput.status,
    };
  };

  const renderTitle = () => {
    switch (action) {
      case "edit":
        return (
          <Title
            title="Edit Trip"
            titleSize="1.5rem"
            gap="0.5rem"
            lineHeight="2.5rem"
          />
        );
      case "create":
        return (
          <Title
            title="Create Trip"
            titleSize="1.5rem"
            gap="0.5rem"
            lineHeight="2.5rem"
          />
        );
      case "view":
        return (
          <Title
            title="View Trip"
            titleSize="1.5rem"
            gap="0.5rem"
            lineHeight="2.5rem"
          />
        );
      default:
        return (
          <Title
            title="Trip Management"
            subtitle="Manage all the trips in your institution"
            titleSize="1.5rem"
            subtitleSize="1rem"
            gap="0.5rem"
            lineHeight="2rem"
          />
        );
    }
  };

  const renderContent = () => {
    switch (action) {
      case "edit":
        return (
          <TripForm
            mode="edit"
            existingData={selectedRowData}
            onSubmit={handleEdit}
            handleReset={handleReset}
            loading={loading}
            setLoading={setLoading}
            input={input}
            setInput={setInput}
            error={error}
            setError={setError}
            role={role}
          />
        );
      case "create":
        return (
          <TripForm
            mode="create"
            onSubmit={handleCreate}
            handleReset={handleReset}
            loading={loading}
            setLoading={setLoading}
            input={input}
            setInput={setInput}
            error={error}
            setError={setError}
          />
        );
      case "view":
        return (
          <TripForm
            mode="view"
            existingData={selectedRowData}
            handleReset={handleReset}
            loading={loading}
            setLoading={setLoading}
            input={input}
            setInput={setInput}
            error={error}
            setError={setError}
          />
        );
      default:
        return (
          <Table
            data={fetchedTripData?.trips || []}
            searchText="Search by trip name..."
            headers={headers}
            showEmptyHeader={true}
            showFilter={true}
            handlePopoverAction={handlePopoverAction}
            onClick={handleOpenCreate}
            module="trips"
            loading={loading}
            showTableAction={
              role === "ADMIN" || role === "SUPERADMIN" || role === "EDITOR"
            }
            height="75vh"
            useBackendPagination={true}
            onPageChange={fetchTrips}
            totalPages={fetchedTripData?.pagination?.totalPages || 1}
            currentPage={pageNumber}
            filters={filters}
            selectedFilters={selectedFilters}
            setSelectedFilters={setSelectedFilters}
            totalRecords={fetchedTripData?.pagination?.total || 0}
            searchTerm={searchTerm}
            setSearchTerm={setSearchTerm}
          />
        );
    }
  };

  return (
    <div className="main-page trips-page">
      <NavBar type="trips" action={action} handleReset={handleReset} />
      <div className="content-area trips-content-area">
        <div className="title-container mobile-hide-page-title">{renderTitle()}</div>
        <div className="main-content-container trips-main-content-container">{renderContent()}</div>
      </div>
      <DeleteModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onDelete={handleDelete}
        itemName={selectedRowData}
        module="trips"
        loading={loading}
      />
      <DeactivationModal
        isOpen={showDeactivationModal}
        onClose={() => setShowDeactivationModal(false)}
        loading={loading}
        onToggle={handleStatus}
        itemName={selectedRowData}
        module="trips"
        action={action}
      />
      {showSnackbar && (
        <div className="notification">
          <SnackBar
            state={showSnackbar}
            setState={setShowSnackbar}
            title={snackbarTitle}
            subtitle={snackbarSubtitle}
            module={snackbarType}
            action={snackbarType}
          />
        </div>
      )}
    </div>
  );
}
